import { useEffect, useRef, useState } from 'react'
import type { MeasurementUiMessage, MeasurementUiOvalState } from '../types'

const HOLD_STILL_MS = 1000
const HOLD_STILL_GRACE_MS = 200

/** Messages that prompt the user to correct something. */
const WARNING_MESSAGES: readonly MeasurementUiMessage[] = [
  'tooClose',
  'tooFar',
  'tooTilted',
  'headUp',
  'headDown',
  'headLeft',
  'headRight',
  'openEyes',
]

type Presentation = {
  message: MeasurementUiMessage | null
  ovalState: MeasurementUiOvalState
}

type PresentationOverride = {
  inputMessage: MeasurementUiMessage
  presentation: Presentation
}

const isWarningMessage = (
  message: MeasurementUiMessage | null
): message is MeasurementUiMessage =>
  message !== null && WARNING_MESSAGES.includes(message)

/**
 * Smooths the transitions around the cyan scanning phase without delaying the
 * capture itself:
 *
 * - a direct `scanning` -> `holdStill` transition gets a short grace period,
 *   so brief tracking fluctuations stay visually cyan;
 * - the one-second hold after a resolved warning is only inserted when that
 *   warning lasted beyond the same grace period.
 *
 * Any other message change cancels the pending transition immediately, so
 * corrective guidance, completion and failures are never held back.
 */
export function useHoldStillTransition(
  message: MeasurementUiMessage | null,
  ovalState: MeasurementUiOvalState
): Presentation {
  const [override, setOverride] = useState<PresentationOverride | null>(null)
  const previousMessageRef = useRef(message)
  const hasWarningPassedGraceRef = useRef(false)
  const isWarning = isWarningMessage(message)

  useEffect(() => {
    if (!isWarning) return

    hasWarningPassedGraceRef.current = false
    const timeout = setTimeout(() => {
      hasWarningPassedGraceRef.current = true
    }, HOLD_STILL_GRACE_MS)

    return () => clearTimeout(timeout)
  }, [isWarning])

  useEffect(() => {
    const previousMessage = previousMessageRef.current
    const wasWarning = isWarningMessage(previousMessage)
    previousMessageRef.current = message

    if (message === 'holdStill' && previousMessage === 'scanning') {
      setOverride({
        inputMessage: message,
        presentation: { message: 'scanning', ovalState: 'scanning' },
      })
      const timeout = setTimeout(() => {
        setOverride(null)
      }, HOLD_STILL_GRACE_MS)

      return () => clearTimeout(timeout)
    }

    const isMeaningfulResolvedWarning =
      message === 'scanning' && wasWarning && hasWarningPassedGraceRef.current

    if (isMeaningfulResolvedWarning) {
      setOverride({
        inputMessage: message,
        presentation: { message: 'holdStill', ovalState: 'initial' },
      })
      const timeout = setTimeout(() => {
        setOverride(null)
      }, HOLD_STILL_MS)

      return () => clearTimeout(timeout)
    }

    setOverride(null)
  }, [message])

  if (override?.inputMessage === message) {
    return override.presentation
  }

  const previousMessage = previousMessageRef.current

  if (message === 'holdStill' && previousMessage === 'scanning') {
    return { message: 'scanning', ovalState: 'scanning' }
  }

  const isMeaningfulResolvedWarning =
    message === 'scanning' &&
    isWarningMessage(previousMessage) &&
    hasWarningPassedGraceRef.current

  if (isMeaningfulResolvedWarning) {
    return { message: 'holdStill', ovalState: 'initial' }
  }

  return { message, ovalState }
}
